import db from "./offlineDb";
import apiTamboUrl from "./api";

/**
 * Encola una operación de escritura para sincronizar cuando haya red.
 *
 * @param {string} tabla - Nombre de la tabla/recurso (e.g. 'animales', 'registros_peso')
 * @param {string} accion - 'create' | 'update' | 'delete'
 * @param {object} payload - Datos de la operación
 * @param {string} endpoint - Endpoint del API para enviar la operación
 * @param {string} method - Método HTTP: 'POST' | 'PATCH' | 'DELETE'
 */
export async function enqueueOp(
  tabla,
  accion,
  payload,
  endpoint,
  method = null
) {
  await db.pendingOps.add({
    tabla,
    accion,
    payload,
    endpoint,
    method:
      method ||
      (accion === "create" ? "POST" : accion === "update" ? "PATCH" : "DELETE"),
    synced: 0,
    createdAt: new Date().toISOString(),
  });
  console.log(`📝 Op encolada: ${accion} en ${tabla}`);
}

/**
 * Envía todas las operaciones pendientes al backend, en orden FIFO.
 * Se detiene en la primera que falle para preservar el orden.
 *
 * @returns {number} Cantidad de operaciones enviadas exitosamente.
 */
export async function flushQueue() {
  const ops = await db.pendingOps.where("synced").equals(0).sortBy("createdAt");

  let sent = 0;

  for (const op of ops) {
    try {
      await sendOp(op);
      await db.pendingOps.update(op.id, { synced: 1 });
      sent++;
    } catch (err) {
      // 1. Desconexión real de red (offline, timeout sin respuesta)
      const isNetworkError =
        !err.response ||
        err.code === "ERR_NETWORK" ||
        err.message?.includes("Network Error") ||
        (typeof navigator !== "undefined" && !navigator.onLine);

      if (isNetworkError) {
        console.warn(
          `⚠️ Flush detenido por pérdida de conexión en op ${op.id}:`,
          err.message
        );
        break;
      }

      // 2. Respuesta HTTP del servidor: verificar si es un conflicto por redundancia/idempotencia
      const status = err.response?.status;
      const data = err.response?.data;
      const detail =
        typeof data?.detail === "string"
          ? data.detail
          : JSON.stringify(data || "");

      const isConflict =
        status === 409 || // 409 Conflict (ej. caravana_duplicada, ciclo ya cerrado)
        (status === 400 &&
          (detail.includes("ya está marcado como muerto") ||
            detail.includes("ya fue registrado") ||
            detail.includes("no tiene una preñez confirmada activa") ||
            detail.includes("ya se encuentra en") ||
            detail.includes("ya existe"))) ||
        (status === 422 &&
          (data?.codigo === "caravana_duplicada" ||
            detail.includes("caravana_duplicada") ||
            detail.includes("ya existe")));

      if (isConflict) {
        console.info(
          `ℹ️ Op ${op.id} (${op.accion} en ${op.tabla}) resuelta: estado ya aplicado previamente en el servidor.`,
          detail
        );
        await db.pendingOps.update(op.id, {
          synced: 1,
          conflictResolved: true,
          conflictDetail: detail,
        });
        sent++;
        continue;
      }

      // 3. Error no recuperable del servidor (401, 403, 500 o validación real fallida)
      console.error(
        `❌ Flush detenido por error de servidor en op ${op.id} [${status}]:`,
        detail
      );
      break;
    }
  }

  if (sent > 0) {
    console.log(`✅ Flush: ${sent}/${ops.length} ops enviadas`);
  }

  return sent;
}

/**
 * Envía una operación individual al backend.
 */
async function sendOp(op) {
  const { endpoint, method, payload } = op;
  if (!endpoint) {
    console.warn("⚠️ Op sin endpoint, marcando como synced:", op);
    return;
  }

  switch (method) {
    case "POST":
      await apiTamboUrl.post(endpoint, payload);
      break;
    case "PATCH":
      await apiTamboUrl.patch(endpoint, payload);
      break;
    case "PUT":
      await apiTamboUrl.put(endpoint, payload);
      break;
    case "DELETE":
      await apiTamboUrl.delete(endpoint);
      break;
    default:
      console.warn(`Método desconocido: ${method}`);
  }
}

/**
 * Devuelve la cantidad de operaciones pendientes.
 */
export async function getPendingCount() {
  return await db.pendingOps.where("synced").equals(0).count();
}
